<script setup lang="ts">
   import { useRoute } from 'vue-router';
   import useAuthStore from '~/stores/auth';
   import { computed } from 'vue';

   const authStore = useAuthStore();
   const route = useRoute();

   const showIpaLogo = computed(() => {
      // Always show on public routes
      if (!route.fullPath.startsWith('/lms')) {
         return true;
      }

      // On /lms routes, check site_settings flag
      return authStore?.user?.site_settings?.show_ipa_logo ?? true;
   });

   const showUkCoachingLogo = computed(() => {
      // Always show on public routes
      if (!route.fullPath.startsWith('/lms')) {
         return true;
      }

      // On /lms routes, check site_settings flag
      return authStore?.user?.site_settings?.show_uk_coaching_1st_4_sport_logo ?? true;
   });

   const showAajpLogo = computed(() => {
      // Always show on public routes
      if (!route.fullPath.startsWith('/lms')) {
         return true;
      }

      // On /lms routes, check site_settings flag
      return authStore?.user?.site_settings?.show_aajp_logo ?? true;
   });
</script>

<template>
   <!-- pb keeps the last row clear of the iPhone home indicator, which overlays the
        bottom of the WebView. 0 on the web. -->
   <footer class="mt-12 bg-brand-footer pb-[env(safe-area-inset-bottom)] text-white">
      <div class="mx-auto w-full max-w-7xl px-4 py-6">
         <div class="flex flex-col items-center gap-3">
            <div class="flex flex-wrap justify-center gap-5">
               <div class="flex w-[90px] items-center">
                  <img src="/img/fip-logo.png" alt="fip logo" />
               </div>
               <div class="flex w-[70px] items-center" v-if="showIpaLogo">
                  <img src="/img/ipa-logo.png" alt="ipa logo" />
               </div>
               <a
                  href="https://www.aajpolo.com/"
                  target="_blank"
                  class="flex w-[90px] items-center overflow-hidden rounded-lg"
                  v-if="showAajpLogo">
                  <img src="/img/aajp-logo.jpg" alt="aajp logo" />
               </a>
               <div class="flex w-[300px] items-center" v-if="showUkCoachingLogo">
                  <img
                     src="/img/uk-coaching-1st4sports-logo.png"
                     alt="uk coaching 1st4sport logo" />
               </div>
            </div>
            <p class="mt-4 text-sm text-white/80">Built to support organisations and participants across the global game.</p>
            <FooterTermlyModal />
            <div class="mt-7 flex flex-col items-center justify-center gap-7">
               <p>support@polo-development.com</p>
               <div class="flex gap-5">
                  <h5>Follow Us:</h5>
                  <ul class="flex gap-3">
                     <li>
                        <a
                           href="https://www.facebook.com/share/1ERMuF3yTQ/"
                           target="_blank"
                           class="flex size-7 w-7 items-center justify-center rounded-md border border-white text-white hover:bg-brand-title hover:text-white"
                           rel="noopener">
                           <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="28"
                              height="28"
                              viewBox="0 0 71 72"
                              fill="none">
                              <path
                                 d="M46.4233 38.6403L47.7279 30.3588H39.6917V24.9759C39.6917 22.7114 40.8137 20.4987 44.4013 20.4987H48.1063V13.4465C45.9486 13.1028 43.7685 12.9168 41.5834 12.8901C34.9692 12.8901 30.651 16.8626 30.651 24.0442V30.3588H23.3193V38.6403H30.651V58.671H39.6917V38.6403H46.4233Z"
                                 fill="currentColor"></path>
                           </svg>
                        </a>
                     </li>

                     <li>
                        <a
                           href="https://x.com/intlpolo"
                           target="_blank"
                           class="flex size-7 w-7 items-center justify-center rounded-md border border-white text-white hover:bg-brand-title hover:text-white"
                           rel="noopener">
                           <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="currentColor"
                              class="bi bi-twitter-x"
                              viewBox="0 0 16 16"
                              id="Twitter-X--Streamline-Bootstrap"
                              height="16"
                              width="16">
                              <path
                                 d="M12.6 0.75h2.454l-5.36 6.142L16 15.25h-4.937l-3.867 -5.07 -4.425 5.07H0.316l5.733 -6.57L0 0.75h5.063l3.495 4.633L12.601 0.75Zm-0.86 13.028h1.36L4.323 2.145H2.865z"
                                 stroke-width="1"></path>
                           </svg>
                        </a>
                     </li>

                     <li>
                        <a
                           href="https://www.instagram.com/intlpolo?igsh=NmY4bnk5ZDN2eG5v"
                           target="_blank"
                           class="flex size-7 w-7 items-center justify-center rounded-md border border-white text-white hover:bg-brand-title hover:text-white"
                           rel="noopener">
                           <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="28"
                              height="28"
                              viewBox="0 0 71 72"
                              fill="none">
                              <path
                                 d="M27.3762 35.7808C27.3762 31.1786 31.1083 27.4468 35.7132 27.4468C40.3182 27.4468 44.0522 31.1786 44.0522 35.7808C44.0522 40.383 40.3182 44.1148 35.7132 44.1148C31.1083 44.1148 27.3762 40.383 27.3762 35.7808ZM22.8683 35.7808C22.8683 42.8708 28.619 48.618 35.7132 48.618C42.8075 48.618 48.5581 42.8708 48.5581 35.7808C48.5581 28.6908 42.8075 22.9436 35.7132 22.9436C28.619 22.9436 22.8683 28.6908 22.8683 35.7808ZM46.0648 22.4346C46.0646 23.0279 46.2404 23.608 46.5701 24.1015C46.8997 24.595 47.3684 24.9797 47.9168 25.2069C48.4652 25.4342 49.0688 25.4939 49.6511 25.3784C50.2334 25.2628 50.7684 24.9773 51.1884 24.5579C51.6084 24.1385 51.8945 23.6041 52.0105 23.0222C52.1266 22.4403 52.0674 21.8371 51.8404 21.2888C51.6134 20.7406 51.2289 20.2719 50.7354 19.942C50.2418 19.6122 49.6615 19.436 49.0679 19.4358H49.0667C48.2708 19.4361 47.5077 19.7522 46.9449 20.3144C46.3821 20.8767 46.0655 21.6392 46.0648 22.4346ZM25.6072 56.1302C23.1683 56.0192 21.8427 55.6132 20.9618 55.2702C19.7939 54.8158 18.9606 54.2746 18.0845 53.4002C17.2083 52.5258 16.666 51.6938 16.2133 50.5266C15.8699 49.6466 15.4637 48.3214 15.3528 45.884C15.2316 43.2488 15.2073 42.4572 15.2073 35.781C15.2073 29.1048 15.2336 28.3154 15.3528 25.678C15.4639 23.2406 15.8731 21.918 16.2133 21.0354C16.668 19.8682 17.2095 19.0354 18.0845 18.1598C18.9594 17.2842 19.7919 16.7422 20.9618 16.2898C21.8423 15.9466 23.1683 15.5406 25.6072 15.4298C28.244 15.3086 29.036 15.2844 35.7132 15.2844C42.3904 15.2844 43.1833 15.3106 45.8223 15.4298C48.2612 15.5408 49.5846 15.9498 50.4677 16.2898C51.6356 16.7422 52.4689 17.2854 53.345 18.1598C54.2211 19.0342 54.7615 19.8682 55.2161 21.0354C55.5595 21.9154 55.9658 23.2406 56.0767 25.678C56.1979 28.3154 56.2221 29.1048 56.2221 35.781C56.2221 42.4572 56.1979 43.2466 56.0767 45.884C55.9656 48.3214 55.5573 49.6462 55.2161 50.5266C54.7615 51.6938 54.2199 52.5266 53.345 53.4002C52.4701 54.2738 51.6356 54.8158 50.4677 55.2702C49.5872 55.6134 48.2612 56.0194 45.8223 56.1302C43.1855 56.2514 42.3934 56.2756 35.7132 56.2756C29.033 56.2756 28.2432 56.2514 25.6072 56.1302ZM25.4001 10.9322C22.7371 11.0534 20.9174 11.4754 19.3282 12.0934C17.6824 12.7316 16.2892 13.5878 14.897 14.977C13.5047 16.3662 12.6502 17.7608 12.0116 19.4056C11.3933 20.9948 10.971 22.8124 10.8497 25.4738C10.7265 28.1394 10.6982 28.9916 10.6982 35.7808C10.6982 42.57 10.7265 43.4222 10.8497 46.0878C10.971 48.7494 11.3933 50.5668 12.0116 52.156C12.6502 53.7998 13.5049 55.196 14.897 56.5846C16.289 57.9732 17.6824 58.8282 19.3282 59.4682C20.9204 60.0862 22.7371 60.5082 25.4001 60.6294C28.0687 60.7506 28.92 60.7808 35.7132 60.7808C42.5065 60.7808 43.3592 60.7526 46.0264 60.6294C48.6896 60.5082 50.5081 60.0862 52.0983 59.4682C53.7431 58.8282 55.1373 57.9738 56.5295 56.5846C57.9218 55.1954 58.7745 53.7998 59.4149 52.156C60.0332 50.5668 60.4575 48.7492 60.5768 46.0878C60.698 43.4202 60.7262 42.57 60.7262 35.7808C60.7262 28.9916 60.698 28.1394 60.5768 25.4738C60.4555 22.8122 60.0332 20.9938 59.4149 19.4056C58.7745 17.7618 57.9196 16.3684 56.5295 14.977C55.1395 13.5856 53.7431 12.7316 52.1003 12.0934C50.5081 11.4754 48.6894 11.0514 46.0284 10.9322C43.3612 10.811 42.5085 10.7808 35.7152 10.7808C28.922 10.7808 28.0687 10.809 25.4001 10.9322Z"
                                 fill="currentColor"></path>
                           </svg>
                        </a>
                     </li>
                  </ul>
               </div>
            </div>
         </div>
      </div>
   </footer>
</template>

<style scoped></style>
